/**
 * OptimalTimesInsightPanel Component
 * 
 * Progressive disclosure UI for optimal time suggestions with three data stages:
 * - No data (0-14 posts): Educational content and industry insights
 * - Sparse data (15-44 posts): Building profile with milestone tracking
 * - Sufficient data (45+ posts): Personalized optimal times
 * 
 * Features:
 * - Platform-specific thresholds and insights
 * - Milestone tracking with progress indicators
 * - Industry fallback recommendations
 * - Auto-expansion logic based on data stage
 * - Comprehensive analytics tracking
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    LinearProgress,
    Collapse,
    IconButton,
    Stack,
    Grid,
    Alert,
    Tooltip,
    CircularProgress,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    Star as StarIcon,
    EmojiEvents as TrophyIcon,
    Psychology as BrainIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    Lightbulb as LightbulbIcon,
    CheckCircle as CheckCircleIcon,
    Lock as LockIcon,
    AccessTime as ClockIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDataSufficiency } from '../../hooks/useOptimalTimes';
import { useTracking } from '../../hooks/useTracking';
import { EnhancedPlatformEvents } from '../../services/analytics/events';
import dayjs from 'dayjs';

// Styled components
const ProgressCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.primary.main}05 100%)`,
}));

const MilestoneChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'isUnlocked'
})(({ theme, isUnlocked }) => ({
    margin: theme.spacing(0.5),
    ...(isUnlocked && {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
        '& .MuiChip-icon': {
            color: theme.palette.success.contrastText,
        }
    }),
    ...(!isUnlocked && {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.grey[600],
        '& .MuiChip-icon': {
            color: theme.palette.grey[600],
        }
    })
}));

const IndustryInsightBox = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        boxShadow: theme.shadows[2],
        borderColor: theme.palette.primary.main,
    }
}));

// Data stage configurations
const DATA_STAGES = {
    NO_DATA: {
        title: "Post regularly to unlock your personalized peak times",
        subtitle: "We need some posting history to analyze your audience engagement patterns",
        icon: LightbulbIcon,
        color: 'info'
    },
    SPARSE_DATA: {
        title: "Building your engagement profile...",
        subtitle: "Great start! Keep posting to unlock more personalized insights",
        icon: TrendingUpIcon,
        color: 'warning'
    },
    SUFFICIENT_DATA: {
        title: "Your personalized optimal times are ready!",
        subtitle: "Based on your posting history and audience engagement patterns",
        icon: StarIcon,
        color: 'success'
    }
};

// Platform-specific industry insights
const PLATFORM_INSIGHTS = {
    twitter: {
        name: 'Twitter',
        peakTimes: ['9:00 AM', '12:00 PM', '5:00 PM'],
        tips: [
            'Tweet during commute hours for maximum visibility',
            'Engage with trending topics for better reach',
            'Use 1-2 hashtags for optimal engagement'
        ],
        audience: 'Professional and news-focused users'
    },
    facebook: {
        name: 'Facebook',
        peakTimes: ['1:00 PM', '3:00 PM', '8:00 PM'],
        tips: [
            'Post when people take breaks during workday',
            'Evening posts get more engagement',
            'Visual content performs 65% better'
        ],
        audience: 'Broad demographic, family and friends'
    },
    instagram: {
        name: 'Instagram',
        peakTimes: ['11:00 AM', '2:00 PM', '5:00 PM'],
        tips: [
            'Lunch break scrolling drives midday engagement',
            'Stories get 3x more views than posts',
            'Use 5-10 relevant hashtags for discovery'
        ],
        audience: 'Visual-first, younger demographics'
    },
    linkedin: {
        name: 'LinkedIn',
        peakTimes: ['8:00 AM', '12:00 PM', '5:00 PM'],
        tips: [
            'Post before work hours for professional audience',
            'Industry insights get 2x more engagement',
            'Tuesday-Thursday are optimal posting days'
        ],
        audience: 'Professional network and B2B'
    },
    tiktok: {
        name: 'TikTok',
        peakTimes: ['6:00 AM', '10:00 AM', '7:00 PM'],
        tips: [
            'Early morning catches global audience',
            'Trending sounds boost discoverability',
            'First 3 seconds determine video success'
        ],
        audience: 'Gen Z and younger millennials'
    }
};

// Milestone definitions
const MILESTONES = [
    {
        key: 'first_insights_unlocked',
        title: 'First Insights',
        description: 'Basic engagement patterns',
        icon: BrainIcon
    },
    {
        key: 'trend_analysis_unlocked',
        title: 'Trend Analysis',
        description: 'Weekly and daily patterns',
        icon: TrendingUpIcon
    },
    {
        key: 'full_profile_unlocked',
        title: 'Full Profile',
        description: 'Personalized optimal times',
        icon: TrophyIcon
    }
];

/**
 * Data Progress Indicator Component
 */
const DataProgressIndicator = ({ dataSufficiency, platform }) => {
    const { trackEvent } = useTracking();
    
    if (!dataSufficiency) return null;
    
    const { stage, progress_percentage, milestones, total_posts, posts_needed_for_next_stage, thresholds } = dataSufficiency;
    
    const handleMilestoneClick = (milestone) => {
        trackEvent(EnhancedPlatformEvents.MILESTONE_CLICKED.name, {
            platform,
            milestone: milestone.key,
            unlocked: milestones[milestone.key],
            currentPosts: total_posts
        });
    };
    
    return (
        <Box>
            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Progress to personalized times
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {Math.round(progress_percentage)}%
                    </Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={progress_percentage} 
                    sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {total_posts} posts • {posts_needed_for_next_stage > 0 ? `${posts_needed_for_next_stage} more needed` : 'Target reached!'}
                </Typography>
            </Box>
            
            {/* Milestones */}
            <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Milestones
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {MILESTONES.map((milestone) => {
                        const isUnlocked = milestones[milestone.key];
                        const threshold = milestones[`milestone_${MILESTONES.indexOf(milestone) + 1}_threshold`];
                        
                        return (
                            <Tooltip 
                                key={milestone.key}
                                title={`${milestone.description} ${isUnlocked ? '✓' : `(${threshold} posts needed)`}`}
                            >
                                <MilestoneChip
                                    icon={isUnlocked ? <CheckCircleIcon /> : <LockIcon />}
                                    label={milestone.title}
                                    size="small"
                                    isUnlocked={isUnlocked}
                                    onClick={() => handleMilestoneClick(milestone)}
                                    clickable
                                />
                            </Tooltip>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
};

/**
 * Industry Insights Card Component
 */
const IndustryInsightsCard = ({ platform }) => {
    const { trackEvent } = useTracking();
    const insight = PLATFORM_INSIGHTS[platform?.toLowerCase()];
    
    const handleInsightView = () => {
        trackEvent(EnhancedPlatformEvents.INDUSTRY_INSIGHT_VIEWED.name, {
            platform,
            insightType: 'peak_times'
        });
    };
    
    // Move useEffect before any early returns to fix React hooks rule
    useEffect(() => {
        if (insight) {
            handleInsightView();
        }
    }, [platform, insight]);
    
    if (!insight) return null;
    
    return (
        <IndustryInsightBox>
            <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    {insight.name} Industry Insights
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Peak engagement times:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {insight.peakTimes.map((time, idx) => (
                            <Chip 
                                key={idx}
                                label={time}
                                size="small"
                                icon={<ClockIcon />}
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Audience: {insight.audience}
                    </Typography>
                </Box>
                
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Pro tips:
                    </Typography>
                    {insight.tips.map((tip, idx) => (
                        <Typography key={idx} variant="caption" display="block" sx={{ ml: 1, mb: 0.5 }}>
                            • {tip}
                        </Typography>
                    ))}
                </Box>
            </CardContent>
        </IndustryInsightBox>
    );
};

/**
 * Preview Insights Component
 */
const PreviewInsights = ({ stage }) => {
    const previewContent = {
        NO_DATA: [
            "📊 See your best posting times based on actual engagement",
            "📈 Track when your audience is most active",
            "⭐ Get confidence scores for each time recommendation",
            "🎯 Compare your performance across different time slots"
        ],
        SPARSE_DATA: [
            "🔍 Daily and weekly engagement patterns emerging",
            "📊 Your audience activity is becoming clearer",
            "⏰ Preliminary optimal times being calculated",
            "📈 Engagement trends starting to show"
        ]
    };
    
    const content = previewContent[stage];
    if (!content) return null;
    
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {stage === 'NO_DATA' ? 'What you\'ll unlock:' : 'What\'s being analyzed:'}
            </Typography>
            <Box sx={{ ml: 1 }}>
                {content.map((item, idx) => (
                    <Typography key={idx} variant="caption" display="block" sx={{ mb: 0.5, opacity: 0.8 }}>
                        {item}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

/**
 * Main OptimalTimesInsightPanel Component
 */
const OptimalTimesInsightPanel = ({ 
    selectedPlatforms = [], 
    onOptimalTimeSelect = () => {} 
}) => {
    const { trackEvent } = useTracking();
    const [expanded, setExpanded] = useState(false);
    
    // Get data sufficiency for the primary platform
    const primaryPlatform = selectedPlatforms[0]?.toLowerCase();
    const { dataSufficiency, loading, error } = useDataSufficiency(primaryPlatform);
    
    // Determine if panel should be auto-expanded
    useEffect(() => {
        if (dataSufficiency && !loading) {
            const shouldAutoExpand = dataSufficiency.stage === 'sparse_data' || dataSufficiency.stage === 'no_data';
            setExpanded(shouldAutoExpand);
        }
    }, [dataSufficiency, loading]);
    
    // Handle panel toggle
    const handleToggle = () => {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        
        trackEvent(EnhancedPlatformEvents.INSIGHT_PANEL_TOGGLED.name, {
            platform: primaryPlatform,
            expanded: newExpanded,
            stage: dataSufficiency?.stage,
            totalPosts: dataSufficiency?.total_posts
        });
    };
    
    // Handle optimal time selection
    const handleOptimalTimeSelect = (time) => {
        trackEvent(EnhancedPlatformEvents.PERSONALIZED_TIME_SELECTED.name, {
            platform: primaryPlatform,
            selectedTime: time,
            source: 'insight_panel'
        });
        
        onOptimalTimeSelect(time);
    };
    
    // Don't render if no platforms selected
    if (selectedPlatforms.length === 0) return null;
    
    // Loading state
    if (loading) {
        return (
            <ProgressCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                        Analyzing your posting patterns...
                    </Typography>
                </Box>
            </ProgressCard>
        );
    }
    
    // Error state
    if (error) {
        return (
            <ProgressCard>
                <Alert severity="info" size="small">
                    Using industry insights while we load your data.
                </Alert>
            </ProgressCard>
        );
    }
    
    if (!dataSufficiency) return null;
    
    const { stage } = dataSufficiency;
    const stageConfig = DATA_STAGES[stage.toUpperCase()] || DATA_STAGES.NO_DATA;
    const StageIcon = stageConfig.icon;
    
    return (
        <ProgressCard>
            {/* Header */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                }}
                onClick={handleToggle}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StageIcon color={stageConfig.color} />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                            Optimal Times Insights
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {primaryPlatform ? `for ${primaryPlatform}` : 'Select a platform'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            
            {/* Collapsible Content */}
            <Collapse in={expanded}>
                <Box sx={{ mt: 2 }}>
                    {/* Stage Message */}
                    <Alert severity={stageConfig.color} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium">
                            {stageConfig.title}
                        </Typography>
                        <Typography variant="caption">
                            {stageConfig.subtitle}
                        </Typography>
                    </Alert>
                    
                    {/* Progress Indicator for sparse and no data stages */}
                    {(stage === 'sparse_data' || stage === 'no_data') && (
                        <>
                            <DataProgressIndicator 
                                dataSufficiency={dataSufficiency} 
                                platform={primaryPlatform}
                            />
                            <Divider sx={{ my: 2 }} />
                        </>
                    )}
                    
                    {/* Industry Insights */}
                    {primaryPlatform && (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {stage === 'sufficient_data' ? 'Industry Benchmarks' : 'Industry Peak Times'}
                            </Typography>
                            <IndustryInsightsCard platform={primaryPlatform} />
                        </>
                    )}
                    
                    {/* Preview Content for early stages */}
                    {(stage === 'no_data' || stage === 'sparse_data') && (
                        <PreviewInsights stage={stage.toUpperCase()} />
                    )}
                    
                    {/* Sufficient Data - Show Optimal Times */}
                    {stage === 'sufficient_data' && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Your personalized optimal times:
                            </Typography>
                            <Alert severity="success" size="small">
                                🎉 Personalized recommendations are now available in Quick Schedule!
                            </Alert>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </ProgressCard>
    );
};

export default OptimalTimesInsightPanel;
